import {
    MOVE_INTERVAL,
    TICK_INTERVAL,
    SLASH_DURATION,
    SLASH_SPAWN_OFFSET,
    SWORD_OFFSET,
    SWORD_DESTROY_COOLDOWN_TICKS,
    SLASH_RATE,
    TileType,
    PlayerState,
} from '@blockworld/shared';
import Phaser from 'phaser';
import type { FallingBlockPredictor } from './FallingBlockPredictor';

export interface CombatPlayerState {
    x: number;
    y: number;
    facing: number;
    isCrouching: boolean;
    isHoldingSword: boolean;
    swordHoldStartTick: number;
    lastSlashSpawnTick: number;
    lastSwordDestroyTick: number;
    pendingSlash: boolean;
    state: PlayerState;
}

export interface SlashRenderState {
    x: number;
    y: number;
    spawnX: number;
    spawnY: number;
    facing: number;
    spawnTime: number;
    duration: number;
    ownerId: string;
    lastCheckedX: number;
}

interface TileAdapter {
    getTile(x: number, y: number): TileType;
    setLocalTile(x: number, y: number, type: TileType): void;
    isBodyPassable(tile: TileType): boolean;
}

interface EnvAdapter {
    localPlayerId(): string | null;
    lastHorizontalMoveTime(): number;
}

export class CombatRenderer {
    public predictedCombatDestroys: Set<string> = new Set();
    public activeSlashes: Map<number, SlashRenderState> = new Map();
    public slashSprites: Map<number, Phaser.GameObjects.Image> = new Map();
    public nextLocalSlashId = -1;

    constructor(
        private readonly tiles: TileAdapter,
        private readonly fallingBlocks: FallingBlockPredictor,
        private readonly env: EnvAdapter,
    ) {}

    public predictSwordTile(player: CombatPlayerState, currentTick: number): void {
        const swordX = player.x + (player.facing * SWORD_OFFSET);
        const swordTileY = player.y + (player.isCrouching ? 0 : 1);
        const tile = this.tiles.getTile(swordX, swordTileY);
        const key = `${swordX},${swordTileY}`;

        // 3-tick cooldown — mirrors server's SWORD_DESTROY_COOLDOWN_TICKS
        const canDestroy = currentTick - player.lastSwordDestroyTick >= SWORD_DESTROY_COOLDOWN_TICKS;
        if (!canDestroy) return;

        if (tile === TileType.LADDER || tile === TileType.LEAVES) {
            this.tiles.setLocalTile(swordX, swordTileY, TileType.STICK);
            this.fallingBlocks.localFallingBlocks.add(key);
            player.lastSwordDestroyTick = currentTick;
            this.predictedCombatDestroys.add(key);
        } else if (tile === TileType.STICK) {
            this.tiles.setLocalTile(swordX, swordTileY, TileType.SKY);
            this.fallingBlocks.localFallingBlocks.delete(key);
            this.fallingBlocks.freshFallingBlocks.delete(key);
            this.fallingBlocks.logInertia.delete(key);
            this.fallingBlocks.propagateLocalInstabilityUp(swordX, swordTileY - 1);
            player.lastSwordDestroyTick = currentTick;
            this.predictedCombatDestroys.add(key);
        } else if (tile === TileType.LOG) {
            this.tiles.setLocalTile(swordX, swordTileY, TileType.PLANK);
            this.fallingBlocks.localFallingBlocks.delete(key);
            this.fallingBlocks.freshFallingBlocks.delete(key);
            this.fallingBlocks.logInertia.delete(key);
            player.lastSwordDestroyTick = currentTick;
            this.predictedCombatDestroys.add(key);
        }
    }

    public spawnLocalSlash(player: CombatPlayerState, now: number): void {
        const spawnY = player.y + (player.isCrouching ? 0 : 1);

        // Check sword tile passability first — mirrors server's spawnSlashForPlayer guard.
        // If a solid block (LOG, DIRT, STONE, etc.) is at the sword position, no slash spawns.
        const swordX = player.x + (player.facing * SWORD_OFFSET);
        const swordTile = this.tiles.getTile(swordX, spawnY);
        if (!this.tiles.isBodyPassable(swordTile) && swordTile !== TileType.RUNE) {
            return; // Sword blocked by solid tile — no slash
        }

        const spawnX = player.x + (player.facing * SLASH_SPAWN_OFFSET);

        // Predict tile interactions at slash spawn position (mirrors server's spawnSlashForPlayer).
        // Specific tile checks run BEFORE the general passability check, matching server order.
        const tile = this.tiles.getTile(spawnX, spawnY);
        const spawnKey = `${spawnX},${spawnY}`;
        if (tile === TileType.LADDER || tile === TileType.LEAVES) {
            this.tiles.setLocalTile(spawnX, spawnY, TileType.STICK);
            this.fallingBlocks.localFallingBlocks.add(spawnKey);
            this.predictedCombatDestroys.add(spawnKey);
            return; // Consumed — no slash
        }
        if (tile === TileType.STICK) {
            this.tiles.setLocalTile(spawnX, spawnY, TileType.SKY);
            this.fallingBlocks.localFallingBlocks.delete(spawnKey);
            this.fallingBlocks.freshFallingBlocks.delete(spawnKey);
            this.fallingBlocks.logInertia.delete(spawnKey);
            this.fallingBlocks.propagateLocalInstabilityUp(spawnX, spawnY - 1);
            this.predictedCombatDestroys.add(spawnKey);
            return; // Consumed — no slash
        }
        if (tile === TileType.LOG) {
            this.tiles.setLocalTile(spawnX, spawnY, TileType.PLANK);
            this.fallingBlocks.localFallingBlocks.delete(spawnKey);
            this.fallingBlocks.freshFallingBlocks.delete(spawnKey);
            this.fallingBlocks.logInertia.delete(spawnKey);
            this.predictedCombatDestroys.add(spawnKey);
            return; // Consumed — no slash
        }
        if (!this.tiles.isBodyPassable(tile)) {
            return; // Solid block — no slash
        }

        // Determine spawn time for tick phase alignment
        // CRITICAL: When spawning right after movement, use `now` directly
        // This ensures slash tick phase matches player's unified tick phase exactly
        // (lastHorizontalMoveTime can drift a few ms from lastUnifiedMoveTime)
        const timeSinceLastMove = now - this.env.lastHorizontalMoveTime();
        const playerJustMoved = timeSinceLastMove < 20; // Within same frame

        // Use `now` when player just moved (for exact phase sync) or when stationary
        const alignedSpawnTime = playerJustMoved || timeSinceLastMove >= MOVE_INTERVAL * 1.5
            ? now : this.env.lastHorizontalMoveTime();

        const localId = this.nextLocalSlashId--;

        console.log(`[SLASH SPAWN] playerX=${player.x}, spawnX=${spawnX}, now=${now}, spawnTime=${alignedSpawnTime}, sinceLast=${now - alignedSpawnTime}, justMoved=${playerJustMoved}`);

        this.activeSlashes.set(localId, {
            x: spawnX,
            y: spawnY,
            spawnX: spawnX,
            spawnY: spawnY,
            facing: player.facing,
            spawnTime: alignedSpawnTime,
            duration: SLASH_DURATION,
            ownerId: this.env.localPlayerId()!,
            lastCheckedX: spawnX,  // Spawn position already checked above
        });
    }

    public updateSlashes(now: number): void {
        const toRemove: number[] = [];

        // Check if local player moved this frame (for syncing local slashes)
        const localPlayerJustMoved = (now - this.env.lastHorizontalMoveTime()) < 20;

        for (const [id, slash] of this.activeSlashes) {
            // Calculate ticks elapsed from spawn time
            const timeElapsed = now - slash.spawnTime;
            const ticksElapsed = Math.floor(timeElapsed / TICK_INTERVAL);

            if (ticksElapsed >= slash.duration) {
                // Slash expired
                toRemove.push(id);
                continue;
            }

            // Calculate expected position based on time
            const expectedX = slash.spawnX + (ticksElapsed * slash.facing);

            // For local player's slashes: sync to player movement while walking
            // When player stops, slash continues independently based on time
            const isLocalSlash = slash.ownerId === this.env.localPlayerId();
            if (isLocalSlash) {
                // Check if player is actively walking (moved within last tick)
                const playerIsWalking = (now - this.env.lastHorizontalMoveTime()) < MOVE_INTERVAL * 1.2;

                if (playerIsWalking) {
                    // Player is walking - only update when player moves (sync)
                    if (localPlayerJustMoved) {
                        slash.x = expectedX;
                    }
                } else {
                    // Player stopped - slash continues independently
                    slash.x = expectedX;
                }
            } else {
                // Other players' slashes use time-based positioning
                slash.x = expectedX;
            }

            // Only check tile interactions when the slash has moved to a NEW position.
            // The spawn position was already checked in spawnLocalSlash(). Re-checking
            // it would catch falling blocks that land in the spawn tile after creation —
            // the server never does this because it advances the slash immediately on spawn tick.
            if (slash.x === slash.lastCheckedX) continue;
            slash.lastCheckedX = slash.x;

            // Check if slash hit a tile — predict effects for local slashes
            const tile = this.tiles.getTile(slash.x, slash.y);
            const hitKey = `${slash.x},${slash.y}`;
            if (tile === TileType.LADDER || tile === TileType.LEAVES) {
                if (isLocalSlash) {
                    this.tiles.setLocalTile(slash.x, slash.y, TileType.STICK);
                    this.fallingBlocks.localFallingBlocks.add(hitKey);
                    this.predictedCombatDestroys.add(hitKey);
                }
                toRemove.push(id);
            } else if (tile === TileType.STICK) {
                if (isLocalSlash) {
                    this.tiles.setLocalTile(slash.x, slash.y, TileType.SKY);
                    this.fallingBlocks.localFallingBlocks.delete(hitKey);
                    this.fallingBlocks.freshFallingBlocks.delete(hitKey);
                    this.fallingBlocks.logInertia.delete(hitKey);
                    this.fallingBlocks.propagateLocalInstabilityUp(slash.x, slash.y - 1);
                    this.predictedCombatDestroys.add(hitKey);
                }
                toRemove.push(id);
            } else if (tile === TileType.TRUNK) {
                // Tree chopping is complex — let server handle
                toRemove.push(id);
            } else if (tile === TileType.LOG) {
                if (isLocalSlash) {
                    this.tiles.setLocalTile(slash.x, slash.y, TileType.PLANK);
                    this.fallingBlocks.localFallingBlocks.delete(hitKey);
                    this.fallingBlocks.freshFallingBlocks.delete(hitKey);
                    this.fallingBlocks.logInertia.delete(hitKey);
                    this.predictedCombatDestroys.add(hitKey);
                }
                toRemove.push(id);
            } else if (!this.tiles.isBodyPassable(tile)) {
                toRemove.push(id);
            }
        }

        // Remove expired/hit slashes
        for (const id of toRemove) {
            this.activeSlashes.delete(id);
            const sprite = this.slashSprites.get(id);
            if (sprite) {
                sprite.destroy();
                this.slashSprites.delete(id);
            }
        }
    }
}
